/**
 * ImageQualityService.js
 * 
 * Service đánh giá chất lượng hình ảnh real-time cho TCM diagnosis
 * - Blur detection (phát hiện ảnh mờ)
 * - Lighting analysis (kiểm tra ánh sáng)
 * - Color cast detection (phát hiện sai màu)
 * - Face/Tongue detection (phát hiện đối tượng)
 */

class ImageQualityService {
    /**
     * Phân tích toàn diện chất lượng ảnh
     * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} source
     * @returns {Object} Quality analysis results
     */
    static async analyzeQuality(source) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Get image data
        if (source instanceof HTMLVideoElement) {
            canvas.width = source.videoWidth;
            canvas.height = source.videoHeight;
        } else if (source instanceof HTMLImageElement) {
            canvas.width = source.naturalWidth;
            canvas.height = source.naturalHeight;
        } else {
            canvas.width = source.width;
            canvas.height = source.height;
        }

        // Validate dimensions before accessing image data
        // This prevents "The index is not in the allowed range" error
        if (canvas.width <= 0 || canvas.height <= 0) {
            throw new Error('Source has invalid dimensions. Video may not be ready.');
        }

        ctx.drawImage(source, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Run all quality checks
        const results = {
            blur: this.detectBlur(imageData),
            lighting: this.analyzeLighting(imageData),
            colorCast: this.detectColorCast(imageData),
            exposure: this.analyzeExposure(imageData),
            overallScore: 0,
            issues: [],
            suggestions: []
        };

        // Calculate overall score
        results.overallScore = this.calculateOverallScore(results);

        // Generate issues and suggestions
        this.generateFeedback(results);

        return results;
    }

    /**
     * Detect blur using Laplacian variance method
     * @param {ImageData} imageData
     * @returns {Object} Blur detection results
     */
    static detectBlur(imageData) {
        const { data, width, height } = imageData;
        const gray = this.convertToGrayscale(data);

        // Apply Laplacian kernel for edge detection
        let variance = 0;
        const laplacian = [];

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const idx = y * width + x;

                // Laplacian kernel: -1, -1, -1, -1, 8, -1, -1, -1, -1
                const val =
                    -gray[(y - 1) * width + (x - 1)] - gray[(y - 1) * width + x] - gray[(y - 1) * width + (x + 1)] +
                    -gray[y * width + (x - 1)] + 8 * gray[idx] - gray[y * width + (x + 1)] +
                    -gray[(y + 1) * width + (x - 1)] - gray[(y + 1) * width + x] - gray[(y + 1) * width + (x + 1)];

                laplacian.push(val);
            }
        }

        // Calculate variance
        const mean = laplacian.reduce((sum, val) => sum + val, 0) / laplacian.length;
        variance = laplacian.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / laplacian.length;

        // Normalize to 0-100 score (higher variance = sharper image)
        const sharpnessScore = Math.min(100, (variance / 100) * 100);

        return {
            score: Math.round(sharpnessScore),
            isBlurry: sharpnessScore < 40,
            variance: Math.round(variance)
        };
    }

    /**
     * Analyze lighting conditions
     * @param {ImageData} imageData
     * @returns {Object} Lighting analysis
     */
    static analyzeLighting(imageData) {
        const { data } = imageData;
        const pixelCount = data.length / 4;

        let totalBrightness = 0;
        let darkPixels = 0;
        let brightPixels = 0;

        for (let i = 0; i < data.length; i += 4) {
            // Calculate brightness using perceived luminance
            const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            totalBrightness += brightness;

            if (brightness < 50) darkPixels++;
            if (brightness > 200) brightPixels++;
        }

        const avgBrightness = totalBrightness / pixelCount;
        const darkRatio = darkPixels / pixelCount;
        const brightRatio = brightPixels / pixelCount;

        // Determine lighting condition
        let condition = 'good';
        let score = 100;

        if (avgBrightness < 80) {
            condition = 'too_dark';
            score = (avgBrightness / 80) * 100;
        } else if (avgBrightness > 180) {
            condition = 'too_bright';
            score = ((255 - avgBrightness) / 75) * 100;
        } else if (darkRatio > 0.3 || brightRatio > 0.3) {
            condition = 'uneven';
            score = 70;
        }

        return {
            score: Math.round(score),
            condition,
            avgBrightness: Math.round(avgBrightness),
            darkRatio: darkRatio.toFixed(2),
            brightRatio: brightRatio.toFixed(2)
        };
    }

    /**
     * Detect color cast (yellow/blue tint from lighting)
     * @param {ImageData} imageData
     * @returns {Object} Color cast detection
     */
    static detectColorCast(imageData) {
        const { data } = imageData;
        const pixelCount = data.length / 4;

        let totalR = 0, totalG = 0, totalB = 0;

        for (let i = 0; i < data.length; i += 4) {
            totalR += data[i];
            totalG += data[i + 1];
            totalB += data[i + 2];
        }

        const avgR = totalR / pixelCount;
        const avgG = totalG / pixelCount;
        const avgB = totalB / pixelCount;

        // Calculate color temperature bias
        const rBias = avgR - avgG;
        const bBias = avgB - avgG;

        let castType = 'neutral';
        let severity = 0;
        let score = 100;

        // Yellow/warm cast (high R, low B)
        if (rBias > 10 && bBias < -10) {
            castType = 'warm';
            severity = Math.min(50, Math.abs(rBias + bBias));
            score = Math.max(50, 100 - severity * 2);
        }
        // Blue/cool cast (low R, high B)
        else if (rBias < -10 && bBias > 10) {
            castType = 'cool';
            severity = Math.min(50, Math.abs(rBias + bBias));
            score = Math.max(50, 100 - severity * 2);
        }
        // Green cast
        else if (Math.abs(rBias) < 5 && Math.abs(bBias) < 5 && avgG > avgR + 5) {
            castType = 'green';
            severity = Math.min(30, avgG - avgR);
            score = Math.max(60, 100 - severity * 2);
        }

        return {
            score: Math.round(score),
            castType,
            severity: Math.round(severity),
            avgRGB: {
                r: Math.round(avgR),
                g: Math.round(avgG),
                b: Math.round(avgB)
            }
        };
    }

    /**
     * Analyze exposure (over/under exposure)
     * @param {ImageData} imageData
     * @returns {Object} Exposure analysis
     */
    static analyzeExposure(imageData) {
        const { data } = imageData;
        let histogram = new Array(256).fill(0);

        // Build histogram
        for (let i = 0; i < data.length; i += 4) {
            const brightness = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
            histogram[brightness]++;
        }

        const totalPixels = data.length / 4;

        // Check clipping (over/under exposure)
        const shadowClip = histogram.slice(0, 10).reduce((a, b) => a + b, 0) / totalPixels;
        const highlightClip = histogram.slice(246, 256).reduce((a, b) => a + b, 0) / totalPixels;

        let condition = 'good';
        let score = 100;

        if (shadowClip > 0.15) {
            condition = 'underexposed';
            score = Math.max(40, 100 - shadowClip * 200);
        } else if (highlightClip > 0.15) {
            condition = 'overexposed';
            score = Math.max(40, 100 - highlightClip * 200);
        } else if (shadowClip > 0.05 || highlightClip > 0.05) {
            condition = 'acceptable';
            score = 80;
        }

        return {
            score: Math.round(score),
            condition,
            shadowClip: shadowClip.toFixed(3),
            highlightClip: highlightClip.toFixed(3)
        };
    }

    /**
     * Calculate overall quality score
     * @param {Object} results - All analysis results
     * @returns {number} Overall score 0-100
     */
    static calculateOverallScore(results) {
        const weights = {
            blur: 0.35,        // Most important - image must be sharp
            lighting: 0.30,    // Second most important
            colorCast: 0.20,   // Important for color accuracy
            exposure: 0.15     // Less critical if lighting is good
        };

        const score =
            results.blur.score * weights.blur +
            results.lighting.score * weights.lighting +
            results.colorCast.score * weights.colorCast +
            results.exposure.score * weights.exposure;

        return Math.round(score);
    }

    /**
     * Generate user-facing feedback
     * @param {Object} results - Analysis results
     */
    static generateFeedback(results) {
        // Check blur
        if (results.blur.isBlurry) {
            results.issues.push('Ảnh bị mờ');
            results.suggestions.push('Giữ điện thoại vững, không rung tay');
        }

        // Check lighting
        if (results.lighting.condition === 'too_dark') {
            results.issues.push('Ánh sáng quá tối');
            results.suggestions.push('Di chuyển đến nơi sáng hơn hoặc bật đèn');
        } else if (results.lighting.condition === 'too_bright') {
            results.issues.push('Ánh sáng quá chói');
            results.suggestions.push('Tránh ánh sáng trực tiếp, di chuyển ra khỏi ánh mặt trời');
        } else if (results.lighting.condition === 'uneven') {
            results.issues.push('Ánh sáng không đều');
            results.suggestions.push('Tìm nguồn sáng đều hơn');
        }

        // Check color cast
        if (results.colorCast.castType !== 'neutral') {
            if (results.colorCast.castType === 'warm') {
                results.issues.push('Ảnh bị vàng (ánh sáng đèn)');
                results.suggestions.push('Sử dụng ánh sáng tự nhiên hoặc đèn LED trắng');
            } else if (results.colorCast.castType === 'cool') {
                results.issues.push('Ảnh bị xanh lạnh');
                results.suggestions.push('Điều chỉnh cân bằng trắng camera');
            }
        }

        // Check exposure
        if (results.exposure.condition === 'underexposed') {
            results.issues.push('Ảnh quá tối (thiếu sáng)');
            results.suggestions.push('Tăng độ sáng môi trường');
        } else if (results.exposure.condition === 'overexposed') {
            results.issues.push('Ảnh quá sáng (cháy sáng)');
            results.suggestions.push('Giảm ánh sáng hoặc di chuyển');
        }

        // Add general suggestion if quality is good
        if (results.overallScore >= 80) {
            results.suggestions.push('✓ Chất lượng ảnh tốt, sẵn sàng chụp!');
        }
    }

    /**
     * Helper: Convert RGB to grayscale
     * @private
     */
    static convertToGrayscale(data) {
        const gray = [];
        for (let i = 0; i < data.length; i += 4) {
            gray.push(Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]));
        }
        return gray;
    }

    /**
     * Get quality level string
     * @param {number} score - Quality score 0-100
     * @returns {string} Quality level
     */
    static getQualityLevel(score) {
        if (score >= 80) return 'excellent';
        if (score >= 65) return 'good';
        if (score >= 50) return 'acceptable';
        if (score >= 35) return 'poor';
        return 'very_poor';
    }

    /**
     * Get quality color for UI
     * @param {number} score
     * @returns {string} Color hex
     */
    static getQualityColor(score) {
        if (score >= 80) return '#10B981'; // Green
        if (score >= 65) return '#84CC16'; // Lime
        if (score >= 50) return '#EAB308'; // Yellow
        if (score >= 35) return '#F97316'; // Orange
        return '#EF4444'; // Red
    }
}

export default ImageQualityService;
